// Authentication service - client-side API calls

import type { User, LoginCredentials, CreateUserInput } from '@/types'
import type { UserRole } from '@/types'

const API_BASE = '/api/auth'

// Login with email and password
export async function login(credentials: LoginCredentials): Promise<User> {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Important: include cookies
    body: JSON.stringify(credentials),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }))
    throw new Error(error.error || 'Login failed')
  }

  const data = await response.json()
  return data.user
}

// Logout - clear session
export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
  })
}

// Get current user (check if authenticated)
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch(`${API_BASE}/me`, {
      credentials: 'include',
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.user
  } catch {
    return null
  }
}

// List all users (for team management)
export async function listUsers(): Promise<User[]> {
  const response = await fetch(`${API_BASE}/users`, {
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to list users' }))
    throw new Error(error.error)
  }

  const data = await response.json()
  return data.users
}

// Create new user
export async function createUser(input: CreateUserInput): Promise<User> {
  const response = await fetch(`${API_BASE}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create user' }))
    throw new Error(error.error)
  }

  const data = await response.json()
  return data.user
}

// Delete user
export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete user' }))
    throw new Error(error.error)
  }
}

// Update user role
export async function updateUserRole(userId: string, role: UserRole): Promise<User> {
  const response = await fetch(`${API_BASE}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ role }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update role' }))
    throw new Error(error.error || 'Failed to update role')
  }

  const data = await response.json()
  return data.user
}

// Request password reset
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

// Reset password with token
export async function resetPassword(
  email: string,
  token: string,
  password: string
): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, token, password }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Reset failed' }))
    throw new Error(error.error || 'Reset failed')
  }

  return response.json()
}
